import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DollarIcon } from '@assets/icons/crm/dollar-icon.svg';

import ModalContainer from '@/components/modals/ModalContainer';
import ModalHeader from '@/components/modals/ModalHeader';
import {
	useGetTargetByIdQuery,
	useSetTargetMutation,
	useUpdateTargetMutation
} from '@/features/crm/v3/target/api/targetApi';
import { toastError, toastSuccess, toastWarning } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { formatToMK, parseSalesValue, validateSalesFormat } from '@/features/crm/utils/helpers';
import LoaderComponent from '@/components/LoaderComponent';
import { setIsPreserveTreeState, setSelectedCRMTarget } from '@/store/actions/crm/CRMCommonAction';

const SetTargetModal = ({ targetId, onCloseModal }) => {
	const filterData = useSelector((store) => store.filterFieldStore);
	const targetItem = useSelector((state) => state?.crmCommonStore?.selectedCRMTarget);
	const dispatch = useDispatch();
	const [gmvTarget, setGmvTarget] = useState('');
	const [designTarget, setDesignTarget] = useState(null);

	const {
		data: targetData,
		refetch,
		isLoading
	} = useGetTargetByIdQuery(targetId, {
		skip: !targetId
	});

	useEffect(() => {
		if (targetData) {
			setGmvTarget(formatToMK(targetData?.targetAmount));
			setDesignTarget(targetData?.targetNoOfNitexDesign);
		}
	}, [targetData]);

	useEffect(() => {
		if (targetId) {
			refetch();
		}
	}, [targetId]);

	const [setTarget] = useSetTargetMutation();
	const [updateTarget] = useUpdateTargetMutation();

	const handleSave = async () => {
		if (!gmvTarget && !designTarget) {
			toastWarning('Please enter at least one target value');
			return;
		}
		if (!validateSalesFormat(gmvTarget)) {
			toastWarning('GMV target must be in K or M format (e.g., $5K or $5M)');
			return;
		}

		try {
			dispatch(openLoader());
			const [year, quarter] = filterData?.quarter[0]?.key.split(' ');
			const baseRequestBody = {
				targetAmount: parseSalesValue(gmvTarget),
				targetNoOfNitexDesign: parseInt(designTarget) || 0
			};

			const response = targetId
				? await updateTarget({
						requestBody: baseRequestBody,
						params: targetId
					}).unwrap()
				: await setTarget({
						...baseRequestBody,
						year: parseInt(year),
						quarter: quarter,
						targetSetTo: targetItem?.userId
					}).unwrap();
			if (response?.success) {
				onCloseModal();
				dispatch(setIsPreserveTreeState(true));
				dispatch(
					setSelectedCRMTarget({
						...targetItem,
						salesTargetId: response?.payload?.salesTargetId,
						targetAmount: response?.payload?.targetAmount,
						targetNoOfNitexDesign: response?.payload?.targetNoOfNitexDesign
					})
				);
				toastSuccess('Target saved successfully');
			}
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		if (name === 'gmvTarget') {
			const cleanValue = value?.toString().replace(/[^0-9.KMkm]/g, '');
			const parts = cleanValue?.split('.');
			if (parts?.length > 2) return;

			if (
				cleanValue?.endsWith('K') ||
				cleanValue?.endsWith('M') ||
				cleanValue?.endsWith('k') ||
				cleanValue?.endsWith('m')
			) {
				if (!validateSalesFormat(`$${cleanValue}`)) {
					toastWarning('Invalid format. Use K for thousands or M for millions (e.g., $1K or $1M)');
					return;
				}
			}
			setGmvTarget(cleanValue);
		} else if (name === 'designTarget') {
			setDesignTarget(value);
		}
	};

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="set-target-modal" onCloseModal={() => {}}>
				<div className="common-popup">
					<ModalHeader
						title={`${targetItem?.userName}'s Target : ${filterData?.quarter[0]?.key}`}
						onClose={onCloseModal}
					/>
					<div className="common-popup-body">
						<div className="form-group">
							<label htmlFor="gmvTarget">GMV target</label>
							<div className="with-icon right-icon">
								<input
									type="text"
									placeholder="Enter sales amount in $1K or $1M"
									name="gmvTarget"
									value={gmvTarget}
									onChange={handleInputChange}
								/>
								<span className="right-2 mt-1">
									<DollarIcon />
								</span>
							</div>
						</div>
						<div className="form-group">
							<label htmlFor="designSalesTarget">Design sales target</label>
							<input
								type="number"
								placeholder="Enter number of styles"
								name="designTarget"
								value={designTarget}
								onChange={handleInputChange}
							/>
						</div>
					</div>
					<div className="modal-footer pt-0">
						<div className="footer-buttons text-right">
							<button className="button gray-outline size32 ml-2" onClick={onCloseModal}>
								Cancel
							</button>
							<button className="button size32 ml-2" onClick={handleSave}>
								Submit
							</button>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default SetTargetModal;
